import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Spin } from 'antd';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import Sort from './Sort';
import Hovered from './Hovered';

const style = {
  wrapper: {
    overflowX: 'auto',
  },
  container: {
    color: `rgba(0, 0, 0, 0.65)`,
    fontSize: 14,
    fontVariant: 'tabular-nums',
    lineHeight: 1.5,
    border: '1px solid #d9d9d9',
    borderRadius: 4,
  },
  row: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  headerCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: 35,
  },
  cell: {
    padding: 10,
    borderBottom: '1px solid #e8e8e8',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    paddingTop: 2,
    paddingBottom: 2,
  },
  header: {
    backgroundColor: `rgba(250, 250, 250)`,
  },
  body: {
    overflowY: 'auto',
    transition: 'all 0.3s',
  },
};

const recalculateColumns = (columns, width) => {
  const predefinedColumns = columns.filter(column => column.width);
  const columnsCount = columns.length - predefinedColumns.length;

  const predefinedWidth = predefinedColumns.reduce(
    (acc, column) => acc + column.width,
    0,
  );

  const defaultWidth = (width - predefinedWidth) / columnsCount;

  return columns.map(column => {
    return {
      ...column,
      width: column.width ? column.width : defaultWidth,
    };
  });
};

const Table = ({
  columns,
  data,
  onScrollEnd,
  onSort,
  isLoading,
  height,
  containerRef,
  initialSort,
}) => {
  const memoizedInitialSort = useMemo(
    () => ({
      key: initialSort.field,
      order: initialSort.order,
    }),
    [initialSort.field, initialSort.order],
  );

  const contentRef = useRef(null);
  const [extendedColumns, setExtendedColumns] = useState([]);
  const [sort, setSort] = useState(memoizedInitialSort);

  const resizeColumns = () => {
    const width = contentRef.current && contentRef.current.clientWidth;
    const extendedColumns = recalculateColumns(columns, width);
    setExtendedColumns(extendedColumns);
  };

  const minWidth = useMemo(() => {
    const BASE_COLUMN_WIDTH = 150;
    const predefinedColumns = columns.filter(column => column.width);
    const predefinedWidth = predefinedColumns.reduce(
      (acc, column) => acc + column.width,
      0,
    );
    return (
      predefinedWidth +
      (columns.length - predefinedColumns.length) * BASE_COLUMN_WIDTH
    );
  }, [columns]);

  useEffect(() => {
    let previousTop = 0;
    const unsubscribe = contentRef.current.addEventListener('scroll', event => {
      const { scrollHeight, scrollTop, clientHeight } = event.target;
      if (!scrollTop || previousTop === scrollTop) {
        return;
      }
      previousTop = scrollTop;
      const isEnd = scrollHeight - clientHeight - 1 < scrollTop;
      if (isEnd) {
        onScrollEnd();
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    resizeColumns();

    const optimized = debounce(resizeColumns, 200);
    window.addEventListener('resize', optimized);
    return () => window.removeEventListener('resize', optimized);
  }, [columns]);

  const keyExtractor = (column, row) => `${column.key}${row.id}`;

  const onSortChanged = (key, order) => {
    setSort({ key, order });
    onSort(key, order);
  };

  return (
    <div style={style.wrapper} ref={containerRef}>
      <Spin spinning={isLoading}>
        <div style={{ ...style.container, minWidth }}>
          <div style={{ ...style.row, ...style.header }}>
            {extendedColumns.map(column => (
              <div
                key={column.key}
                style={{
                  width: column.width,
                  ...style.cell,
                  ...style.headerCell,
                }}
              >
                {column.title}
                {column.isSortable && (
                  <Sort onSort={onSortChanged} index={column.key} sort={sort} />
                )}
              </div>
            ))}
          </div>

          <div
            style={{ ...style.body, height }}
            ref={contentRef}
            className="c-table-overflow-body"
          >
            {data.map(row => (
              <Hovered key={row.id} style={style.row} data-test="table-rows">
                {extendedColumns.map(column => {
                  const item = row[column.key];
                  const render = column.render
                    ? () => column.render(item, row)
                    : () => item;
                  return (
                    <div
                      key={keyExtractor(column, row)}
                      style={{ width: column.width, ...style.cell }}
                    >
                      {render()}
                    </div>
                  );
                })}
              </Hovered>
            ))}
          </div>
        </div>
      </Spin>
    </div>
  );
};

Table.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onScrollEnd: PropTypes.func.isRequired,
  onSort: PropTypes.func,
  isLoading: PropTypes.bool.isRequired,
  height: PropTypes.number.isRequired,
  containerRef: PropTypes.shape({}).isRequired,
  initialSort: PropTypes.shape({
    field: PropTypes.string,
    order: PropTypes.string,
  }),
};

Table.defaultProps = {
  onSort: () => {},
  initialSort: {},
};

export default React.memo(Table);
